import { queueMacrotask } from 'module/helpers/macrotask';
import { useTranslation } from 'module/hooks/use-translation';
import { Form } from 'module/ui/components/atoms/form';
import { InputField } from '@rocketlane/ui';
import { GenerateAsOption, GroupByOption } from 'api/custom-section/types';
import { FC, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAutoFocusOnTitle } from '../../../shared/auto-focus-title';
import { SharedConfigurationBar } from '../../../shared/configuration-bar';
import { SectionCard, SectionDivider } from '../../../shared/section-styles';
import { CompanyPageDerivedSectionSources } from '../section-sources/section-sources';
import { useSourcesUpdaterState } from '../section-sources/source-updater-state';
import { Configuration } from '../types';
import { defaultConfiguration } from '../types';
import { PromptInputFormValues } from '../types';
import { CompanyPageSourceDerivedSectionComponentProps } from '../types';
import { formValuesIntoAttributes } from '../utils';

interface Props extends CompanyPageSourceDerivedSectionComponentProps {
  onEditDone: () => void;
}
export const CompanyPageSourceDerivedSectionPromptInput: FC<Props> = (
  props,
) => {
  const {
    node,
    allowResolve,
    onDone,
    onEditDone,
    pageType,
    pageTriggerListFilter,
  } = props;
  const titleRef = useAutoFocusOnTitle();
  const titlePlaceholder = useTranslation(
    'rich_text_editor.sections.action_section.title_placeholder',
  );
  const [form] = Form.useForm<PromptInputFormValues>();
  const [hasError, setHasError] = useState(false);
  const [configuration, setConfiguration] =
    useState<Configuration>(defaultConfiguration);
  const onValuesChange = useCallback(() => {
    queueMacrotask(() => {
      form
        .validateFields()
        .then((values) => {
          setHasError(false);
          setConfiguration(values.config);
        })
        .catch(() => {
          setHasError(true);
        });
    });
  }, [form]);

  useEffect(() => {
    form.setFieldsValue({
      title: node.attrs.title,
      config: {
        ...defaultConfiguration,
        ...node.attrs.config,
      },
    });
    onValuesChange();
  }, [form, node.attrs.config, node.attrs.title, onValuesChange]);

  const onConfigurationChange = useCallback(
    (changedValues: Partial<Configuration>) => {
      form.setFieldsValue({
        config: {
          ...form.getFieldsValue().config,
          ...changedValues,
        },
      });
      onValuesChange();
    },
    [form, onValuesChange],
  );

  const {
    hasSourcePageTemplateSectionsUpdater,
    invokeSourcePageTemplateSectionsUpdater,
    sourcesUpdateInProgress,
    closePendingUpdater,
  } = useSourcesUpdaterState({ ...props, onConfigurationChange });
  const [actionLoading, setActionLoading] = useState(false);
  const onSubmit = useCallback(() => {
    form.validateFields().then((values) => {
      setActionLoading(true);
      closePendingUpdater();
      onDone(formValuesIntoAttributes(values, node.attrs)).then(() => {
        setActionLoading(false);
        onEditDone();
      });
    });
  }, [form, node.attrs, onDone, onEditDone, closePendingUpdater]);
  const onRemove = useCallback(() => {
    closePendingUpdater();
    props.onRemove();
  }, [closePendingUpdater, props]);

  return (
    <SectionCard $active={sourcesUpdateInProgress}>
      <Form form={form} layout="vertical" onValuesChange={onValuesChange}>
        <Form.Item
          name="title"
          label={
            <FormattedMessage id="rich_text_editor.sections.action_section.default_title" />
          }
          requiredMark={'optional'}
          rules={[
            {
              required: true,
              message: <FormattedMessage id="input.required" />,
            },
          ]}
        >
          <InputField
            type="text"
            ref={titleRef}
            placeholder={titlePlaceholder}
          />
        </Form.Item>
        <Form.Item name="config" hidden />
      </Form>
      <SharedConfigurationBar
        prompt={configuration}
        setPrompt={onConfigurationChange}
        isActionLoading={actionLoading}
        onCancel={onRemove}
        onSubmit={onSubmit}
        validation={{
          isDisabled: hasError,
        }}
        allowResolve={allowResolve}
        defaultValues={{
          groupBy: GroupByOption.Topics,
          generateAs: GenerateAsOption.Paragraph,
        }}
        options={{
          groupBy: Object.values(GroupByOption),
          generateAs: Object.values(GenerateAsOption),
        }}
        pageType={pageType}
        pageTriggerListFilter={pageTriggerListFilter}
      />
      <SectionDivider />
      <CompanyPageDerivedSectionSources
        sources={configuration.sourcePageTemplateSections}
        onClick={
          hasSourcePageTemplateSectionsUpdater
            ? () =>
                invokeSourcePageTemplateSectionsUpdater({
                  ...node,
                  attrs: hasError
                    ? {
                        ...node.attrs,
                        config: {
                          ...node.attrs.config,
                          sourcePageTemplateSections:
                            configuration.sourcePageTemplateSections,
                        },
                      }
                    : formValuesIntoAttributes(
                        form.getFieldsValue(),
                        node.attrs,
                      ),
                })
            : undefined
        }
      />
    </SectionCard>
  );
};
